import { LIMITS } from '../data/limits';
import { ACTION_VERBS, WEAK_OPENERS } from '../data/vocabulary';
import {
  charCount,
  findPhrases,
  firstWord,
  gradient,
  hasMetric,
  normalise,
  ratio,
  toBullets,
  wordCount,
} from '../text';
import type { ExperienceEntry, Rule, RuleContext } from '../types';
import { defineRule, findingsFor, flag, pass, skip } from './helpers';

/**
 * Experience rules.
 *
 * The Experience section is where a profile either proves its claims or does
 * not. Most of these rules therefore judge *bullets*, not entries: a role with
 * three sentences of duties is weaker than a role with two bullets carrying a
 * number, regardless of the job title.
 *
 * Bullets are derived from each entry's `description` by `toBullets()` — we do
 * not store them, so there is exactly one source of truth (see `types.ts`).
 *
 * Note on ordering: the profile form lists roles newest-first and there are no
 * dates in `ExperienceEntry`, so `experience[0]` is treated as the most recent
 * role. If dates are ever added, this is the assumption to revisit.
 */

const f = findingsFor('experience');

const ACTION_VERB_SET = new Set(ACTION_VERBS);

/**
 * Conservative passive-voice detector: a form of "to be" followed by a past
 * participle. Deliberately narrow — "was tasked with" is worth flagging, but a
 * looser pattern starts flagging legitimate sentences, and a false positive on
 * someone's real accomplishment is worse than a missed one.
 *
 * Regular participles are matched by their `-ed`/`-en` ending; the common
 * irregular ones have to be listed, since "was built" is exactly the phrasing
 * this rule exists to catch and no suffix rule will find it.
 */
const IRREGULAR_PARTICIPLES = [
  'built',
  'made',
  'led',
  'sent',
  'kept',
  'held',
  'run',
  'begun',
  'brought',
  'bought',
  'taught',
  'sought',
  'won',
  'set',
  'put',
  'read',
  'met',
  'left',
  'dealt',
  'felt',
  'found',
  'told',
  'sold',
  'paid',
  'lost',
  'spent',
  'cut',
  'shut',
].join('|');

const PASSIVE = new RegExp(
  `\\b(?:was|were|been|being)\\s+(?:\\w+ly\\s+)?(?:\\w+(?:ed|en)|${IRREGULAR_PARTICIPLES})\\b`,
  'i'
);

/** Every non-empty entry with its bullets already derived. */
function entriesWithBullets(ctx: RuleContext): { entry: ExperienceEntry; bullets: string[] }[] {
  return ctx.profile.experience
    .filter((entry) => normalise(entry.title) || normalise(entry.organisation) || entry.description)
    .map((entry) => ({ entry, bullets: toBullets(entry.description) }));
}

/** Flattened bullets across every entry — the unit most rules judge. */
function allBullets(ctx: RuleContext): string[] {
  return entriesWithBullets(ctx).flatMap((item) => item.bullets);
}

/** A short label for an entry, for use in `evidence`. */
function describe(entry: ExperienceEntry): string {
  const title = normalise(entry.title);
  const org = normalise(entry.organisation);
  if (title && org) return `${title} — ${org}`;
  return title || org || 'Untitled role';
}

export const experienceRules: readonly Rule[] = [
  defineRule({
    id: 'experience.missing',
    section: 'experience',
    weight: 3,
    evaluate(ctx) {
      if (entriesWithBullets(ctx).length > 0) return pass();

      // Students often think they have nothing to put here, so the fix has to
      // say explicitly that coursework and projects belong in this section.
      return flag(
        0,
        f(
          'missing',
          'critical',
          'You have no experience entries',
          'Experience is the section recruiters read first and the one LinkedIn weights most heavily in search. An empty one reads as no track record at all.',
          'Add anything where you did the work: internships, freelance jobs, part-time work, university projects, hackathons, club or society roles, and open-source contributions all belong here.'
        )
      );
    },
  }),

  defineRule({
    id: 'experience.incomplete-entry',
    section: 'experience',
    weight: 1,
    evaluate(ctx) {
      const items = entriesWithBullets(ctx);
      if (items.length === 0) return skip();

      const incomplete = items.filter(
        (item) => !normalise(item.entry.title) || !normalise(item.entry.organisation)
      );
      if (incomplete.length === 0) return pass();

      return flag(
        gradient(incomplete.length, items.length, 0),
        f(
          'incomplete-entry',
          'important',
          `${incomplete.length} of your ${items.length} entries is missing a title or organisation`,
          'LinkedIn matches job titles and company names against recruiter searches. An entry missing either one is largely invisible to those searches, and it looks unfinished to a human reader.',
          'Fill in both fields for every entry. For a personal project, the organisation can be "Personal project" or the course or club name.',
          incomplete.map((item) => describe(item.entry))
        )
      );
    },
  }),

  defineRule({
    id: 'experience.no-description',
    section: 'experience',
    weight: 2,
    evaluate(ctx) {
      const items = entriesWithBullets(ctx);
      if (items.length === 0) return skip();

      const empty = items.filter((item) => item.bullets.length === 0);
      if (empty.length === 0) return pass();

      return flag(
        gradient(empty.length, items.length, 0),
        f(
          'no-description',
          'critical',
          `${empty.length} of your roles has no description`,
          'A job title alone tells a reader almost nothing — two people with the same title can have done completely different work. The description is where you prove which one you are.',
          'Add 2–4 bullets per role, each starting with a strong verb and ideally carrying a number.',
          empty.map((item) => describe(item.entry))
        )
      );
    },
  }),

  defineRule({
    id: 'experience.thin-recent',
    section: 'experience',
    weight: 2,
    evaluate(ctx) {
      const items = entriesWithBullets(ctx);
      const recent = items[0];
      if (!recent) return skip();
      // A role with nothing at all is `experience.no-description`'s problem.
      if (recent.bullets.length === 0) return skip();
      if (recent.bullets.length >= LIMITS.bullet.minPerRole) return pass();

      return flag(
        0.4,
        f(
          'thin-recent-role',
          'important',
          'Your most recent role has only one bullet',
          'Your most recent role is the one a reader assumes is most representative of you now, and it gets the most attention. One line cannot carry that weight.',
          `Expand it to at least ${LIMITS.bullet.minPerRole} bullets — ideally three or four. Cover what you owned, what you built or changed, and the result.`,
          [describe(recent.entry)]
        )
      );
    },
  }),

  defineRule({
    id: 'experience.weak-openers',
    section: 'experience',
    weight: 3,
    evaluate(ctx) {
      const bullets = allBullets(ctx);
      if (bullets.length === 0) return skip();

      const matched = bullets.flatMap((bullet) => findPhrases(bullet, WEAK_OPENERS));
      const unique = [...new Set(matched)];
      if (unique.length === 0) return pass();

      return flag(
        gradient(unique.length, 4, 0),
        f(
          'weak-openers',
          'important',
          `Your bullets use duty language ${unique.length === 1 ? 'once' : `${unique.length} times`}`,
          'Phrases like "responsible for" describe what you were assigned, not what changed because you were there. Two candidates can be responsible for the same thing and have delivered wildly different results.',
          'Replace the duty with the outcome. "Responsible for social media" becomes "Grew the society\'s Instagram from 400 to 2,300 followers in one semester".',
          unique
        )
      );
    },
  }),

  defineRule({
    id: 'experience.action-verbs',
    section: 'experience',
    weight: 2,
    evaluate(ctx) {
      const bullets = allBullets(ctx);
      if (bullets.length === 0) return skip();

      const weak = bullets.filter((bullet) => !ACTION_VERB_SET.has(firstWord(bullet)));
      if (weak.length === 0) return pass();

      const strongRatio = ratio(bullets.length - weak.length, bullets.length);
      // Half the bullets opening with a strong verb is a reasonable bar; every
      // single one is not, since some bullets legitimately open with context.
      if (strongRatio >= 0.5) return pass();

      return flag(
        gradient(strongRatio, 0, 0.5),
        f(
          'no-action-verbs',
          'important',
          `${weak.length} of your ${bullets.length} bullets does not start with a strong verb`,
          'The first word of a bullet is the one that gets read when someone is skimming. A verb like "Built" or "Reduced" front-loads the achievement; anything else buries it.',
          'Start each bullet with a past-tense action verb: Built, Led, Reduced, Automated, Launched, Analysed, Delivered.',
          weak.slice(0, 5)
        )
      );
    },
  }),

  defineRule({
    id: 'experience.quantification',
    section: 'experience',
    weight: 3,
    evaluate(ctx) {
      const bullets = allBullets(ctx);
      if (bullets.length === 0) return skip();

      const quantified = bullets.filter((bullet) => hasMetric(bullet));
      const currentRatio = ratio(quantified.length, bullets.length);
      const target = LIMITS.experience.quantifiedRatioTarget;
      if (currentRatio >= target) return pass();

      const percent = Math.round(currentRatio * 100);

      // Weighted highest in this section on purpose: adding numbers is the
      // single highest-leverage change most people can make to a profile.
      return flag(
        gradient(currentRatio, 0, target),
        f(
          'no-quantification',
          'critical',
          `Only ${percent}% of your bullets contain a number`,
          'Numbers are what separate a claim from a description. "Improved performance" could mean anything; "cut page load from 4s to 1.2s" is checkable, memorable, and gives an interviewer something to ask about.',
          `Get at least ${Math.round(target * 100)}% of your bullets carrying a figure. If you did not measure it, estimate honestly and say so: users, hours saved, percentage change, team size, budget, or frequency all work.`,
          bullets.filter((bullet) => !hasMetric(bullet)).slice(0, 4)
        )
      );
    },
  }),

  defineRule({
    id: 'experience.bullet-length',
    section: 'experience',
    weight: 1,
    evaluate(ctx) {
      const bullets = allBullets(ctx);
      if (bullets.length === 0) return skip();

      const long = bullets.filter((bullet) => wordCount(bullet) > LIMITS.bullet.maxWords);
      if (long.length === 0) return pass();

      return flag(
        gradient(long.length, bullets.length, 0),
        f(
          'bullet-too-long',
          'polish',
          `${long.length} of your bullets ${long.length === 1 ? 'runs' : 'run'} over ${LIMITS.bullet.maxWords} words`,
          'Long bullets get skimmed and the point in the middle gets missed. Profiles are read in seconds, not minutes.',
          `Split each long bullet into two, or cut it to one clear claim under ${LIMITS.bullet.maxWords} words. If it contains two achievements, it should be two bullets.`,
          long.slice(0, 3).map((bullet) => `${wordCount(bullet)} words: ${bullet.slice(0, 90)}…`)
        )
      );
    },
  }),

  defineRule({
    id: 'experience.passive-voice',
    section: 'experience',
    weight: 1,
    evaluate(ctx) {
      const bullets = allBullets(ctx);
      if (bullets.length === 0) return skip();

      const passive = bullets.filter((bullet) => PASSIVE.test(bullet));
      if (passive.length === 0) return pass();

      return flag(
        gradient(passive.length, bullets.length, 0),
        f(
          'passive-voice',
          'polish',
          `${passive.length} of your bullets ${passive.length === 1 ? 'is' : 'are'} in the passive voice`,
          'The passive voice removes you from your own achievement. "The dashboard was built" leaves open who built it — which is the one thing the bullet exists to establish.',
          'Rewrite with yourself as the subject: "A dashboard was built to track sales" becomes "Built a dashboard that tracks sales across 12 regions".',
          passive.slice(0, 3)
        )
      );
    },
  }),

  defineRule({
    id: 'experience.description-length',
    section: 'experience',
    weight: 1,
    evaluate(ctx) {
      const items = entriesWithBullets(ctx);
      if (items.length === 0) return skip();

      const over = items.filter(
        (item) => charCount(item.entry.description) > LIMITS.roleDescription.hard
      );
      if (over.length === 0) return pass();

      return flag(
        0.3,
        f(
          'description-too-long',
          'important',
          `${over.length} role description exceeds LinkedIn's ${LIMITS.roleDescription.hard}-character limit`,
          `LinkedIn stops accepting text past ${LIMITS.roleDescription.hard} characters per role, so the end of these descriptions is being silently discarded.`,
          `Cut each to under ${LIMITS.roleDescription.hard} characters. Keep the bullets with numbers in them and drop the ones that only describe duties.`,
          over.map(
            (item) => `${describe(item.entry)} (${charCount(item.entry.description)} characters)`
          )
        )
      );
    },
  }),
];
